import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HiClock,
  HiShieldCheck,
  HiKey,
  HiEnvelope,
  HiUser,
  HiTrash,
  HiArrowRightOnRectangle,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { getSecurityLogs } from '../../services/settingsService';
import { toast } from '../../utils/toast';

const actionIcons = {
  profile_updated: HiUser,
  email_changed: HiEnvelope,
  password_changed: HiKey,
  '2fa_enabled': HiShieldCheck,
  '2fa_disabled': HiShieldCheck,
  account_deleted: HiTrash,
  login: HiArrowRightOnRectangle,
};

const actionColors = {
  profile_updated: 'text-blue-600 bg-blue-50',
  email_changed: 'text-orange-600 bg-orange-50',
  password_changed: 'text-green-600 bg-green-50',
  '2fa_enabled': 'text-green-600 bg-green-50',
  '2fa_disabled': 'text-red-600 bg-red-50',
  account_deleted: 'text-red-600 bg-red-50',
  login: 'text-blue-600 bg-blue-50',
};

export default function SecurityLogs() {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      loadLogs();
    }
  }, [currentUser]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await getSecurityLogs(currentUser.uid);
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load security logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = timestamp => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatMetadata = metadata => {
    if (!metadata || typeof metadata !== 'object') return null;

    const formatted = [];

    // Handle fieldsUpdated array
    if (metadata.fieldsUpdated && Array.isArray(metadata.fieldsUpdated)) {
      const fieldLabels = metadata.fieldsUpdated.map(field => {
        // Convert camelCase to readable format
        return field
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
      });
      formatted.push(`Updated fields: ${fieldLabels.join(', ')}`);
    }

    // Handle other common metadata fields
    if (metadata.from && metadata.to) {
      formatted.push(`Changed from "${metadata.from}" to "${metadata.to}"`);
    } else if (metadata.from) {
      formatted.push(`Previous value: ${metadata.from}`);
    } else if (metadata.to) {
      formatted.push(`New value: ${metadata.to}`);
    }

    // Handle IP address
    if (metadata.ipAddress) {
      formatted.push(`IP Address: ${metadata.ipAddress}`);
    }

    // Handle user agent
    if (metadata.userAgent) {
      // Extract browser name from user agent
      const browserMatch = metadata.userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
      if (browserMatch) {
        formatted.push(`Browser: ${browserMatch[1]}`);
      }
    }

    // Handle device info
    if (metadata.device) {
      formatted.push(`Device: ${metadata.device}`);
    }

    // Handle location if available
    if (metadata.location) {
      formatted.push(`Location: ${metadata.location}`);
    }

    // Handle any other simple key-value pairs
    Object.keys(metadata).forEach(key => {
      if (
        !['fieldsUpdated', 'from', 'to', 'ipAddress', 'userAgent', 'device', 'location'].includes(
          key
        ) &&
        typeof metadata[key] !== 'object'
      ) {
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
        formatted.push(`${label}: ${metadata[key]}`);
      }
    });

    return formatted.length > 0 ? formatted : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-12 shadow-lg">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-blue-600 border-t-transparent"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/30">
              <HiClock className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-black">Security Activity</h2>
              <p className="text-sm text-gray-600">Monitor your account security events</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadLogs}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
          >
            Refresh
          </motion.button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        {logs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-12 text-center"
          >
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <HiClock className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-black">No security logs yet</p>
            <p className="mt-2 text-sm text-gray-600">Your account activity will appear here</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {logs.map((log, index) => {
              const Icon = actionIcons[log.action] || HiShieldCheck;
              const colorClass = actionColors[log.action] || 'text-gray-600 bg-gray-50';

              return (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md"
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${colorClass}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-black">{log.details}</p>
                    <p className="mt-1 text-xs text-gray-600">{formatDate(log.timestamp)}</p>
                    {(() => {
                      const formattedMetadata = formatMetadata(log.metadata);
                      return formattedMetadata ? (
                        <div className="mt-2 space-y-1 rounded-lg bg-gray-50 p-3">
                          {formattedMetadata.map((item, idx) => (
                            <p key={idx} className="text-xs text-gray-600">
                              {item}
                            </p>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
